import CloseIcon from "@mui/icons-material/Close"
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement, useMemo } from "react"

import {
    formatDemandDeviceForApplication,
    formatGenDeviceForApplication,
} from "./formatters"
import DemandDeviceSearchPanel from "./panels/demandDeviceSearchPanel"
import GenDeviceSearchPanel from "./panels/genDeviceSearchPanel"
import { Datasets, LCTOnlyDatasets } from "../../constants/enums"
import { SPLIT_PHASE_ASSUMED } from "../../constants/generalConstants"
import { IDemDatabaseSearchResult } from "../../services/middleware/mappers/searchDemandDevicesMapper"
import { IDeviceSearchResult } from "../../services/middleware/types/singleApplication/searchLctTypes"
import { SupplyPhases } from "../submitApplication/single/types/formGeneratorTypes"
import { IDatabaseSearchResult as IGenDatabaseSearchResult } from "../tables/ttr/genSearchResults"

const DEMAND_DATASETS: ReadonlyArray<string> = [
    LCTOnlyDatasets.HP,
    LCTOnlyDatasets.EVAC,
    LCTOnlyDatasets.EVDC,
    LCTOnlyDatasets.EB,
    LCTOnlyDatasets.ACU,
    LCTOnlyDatasets.HP_DRAFT,
    LCTOnlyDatasets.EVAC_DRAFT,
    LCTOnlyDatasets.EVDC_DRAFT,
    LCTOnlyDatasets.EB_DRAFT,
    LCTOnlyDatasets.ACU_DRAFT,
]

export interface DeviceSearchDialogProps {
    open: boolean
    onClose: () => void
    /** Dataset to query — derived by the caller from the row's selected LCT.
     * Demand datasets (HP/EVAC/EVDC) route to the demand panel; everything
     * else routes to the gen panel. */
    dataset: Datasets | LCTOnlyDatasets | string
    /** Caller's supply phase. When `SPLIT_PHASE_ASSUMED`, no phase lock is
     * applied (user can filter freely). Otherwise the dialog locks the
     * panel's phase filter to this value. */
    phases?: SupplyPhases
    /** When the gen panel is rendered, restricts results to this category
     * (e.g. battery / inverter / EV charger). Ignored for demand. */
    categoryId?: string | number
    /** Invoked with the formatted, application-form-shaped result on
     * selection. The dialog auto-closes after firing. */
    onDeviceSelected: (device: IDeviceSearchResult) => void
}

/**
 * Replacement for the legacy `DeviceSearchPopup`. Picks between the gen and
 * demand search panels based on the supplied dataset, applies caller-driven
 * locked filters, and normalises the picked row into the
 * {@link IDeviceSearchResult} contract the application form already
 * consumes.
 */
const DeviceSearchDialog = ({
    open,
    onClose,
    dataset,
    phases,
    categoryId,
    onDeviceSelected,
}: DeviceSearchDialogProps): ReactElement => {
    const theme = useTheme()

    const isDemand = useMemo(
        () => DEMAND_DATASETS.includes(String(dataset)),
        [dataset]
    )

    const lockedPhaseId =
        phases && phases !== SPLIT_PHASE_ASSUMED ? phases : undefined

    const handleGenSelect = (row: IGenDatabaseSearchResult): void => {
        onDeviceSelected(formatGenDeviceForApplication(row))
        onClose()
    }

    const handleDemandSelect = (row: IDemDatabaseSearchResult): void => {
        onDeviceSelected(formatDemandDeviceForApplication(row))
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    height: "90vh",
                    maxHeight: "900px",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1,
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Typography variant="h6" component="div" fontWeight="bold">
                    <span style={{ color: theme.palette.text.secondary }}>
                        Device Search
                    </span>
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="close device search"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 2,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    {isDemand ? (
                        <DemandDeviceSearchPanel
                            dataset={dataset as LCTOnlyDatasets}
                            locked={
                                lockedPhaseId !== undefined
                                    ? { phaseId: lockedPhaseId }
                                    : undefined
                            }
                            onSelect={handleDemandSelect}
                        />
                    ) : (
                        <GenDeviceSearchPanel
                            locked={{
                                ...(categoryId !== undefined && {
                                    categoryId,
                                }),
                                ...(lockedPhaseId !== undefined && {
                                    phaseId: lockedPhaseId,
                                }),
                            }}
                            onSelect={handleGenSelect}
                        />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

DeviceSearchDialog.defaultProps = {
    phases: undefined,
    categoryId: undefined,
}

export default DeviceSearchDialog
